// File: components/audio/instruments.ts
import * as Tone from 'tone';

export interface GameAudioContext {
    kick: Tone.MembraneSynth;
    snare: Tone.NoiseSynth;
    clap: Tone.NoiseSynth;
    hihat: Tone.MetalSynth;
    crash: Tone.MetalSynth;
    bass: Tone.FMSynth;
    sub: Tone.MonoSynth;
    pads: Tone.PolySynth;
    piano: Tone.PolySynth;
    lead: Tone.Synth;
    pluck: Tone.PolySynth;

    midiLead: Tone.PolySynth;
    midiPiano: Tone.PolySynth;
    midiBass: Tone.PolySynth;
    midiPads: Tone.PolySynth;
    midiStrings: Tone.PolySynth;
    midiBrass: Tone.PolySynth;
    midiWind: Tone.PolySynth;

    uiSynth: Tone.PolySynth;
    noiseSynth: Tone.NoiseSynth;

    dist: Tone.Distortion;
    delay: Tone.FeedbackDelay;
    clapDelay: Tone.FeedbackDelay; // EXPOSED FOR CLEANUP
    clapFilter: Tone.Filter;       // EXPOSED FOR CLEANUP
    reverb: Tone.Reverb;
    sectionFilter: Tone.Filter;
    masterFilter: Tone.Filter;
    compressor: Tone.Compressor;
    limiter: Tone.Limiter;
    width: Tone.StereoWidener;
    meter: Tone.Meter;

    highPass: Tone.Filter;
    masterGain: Tone.Gain;
}

export const initializeAudioContext = async (context?: Tone.Context): Promise<GameAudioContext> => {
    // 0. Use provided context or global context
    const effectiveContext = context || Tone.context;

    // 0. Master Gain for easy cleanup
    const masterGain = new Tone.Gain({ context: effectiveContext, gain: 1 });

    // FORCE NATIVE CONNECTION: Bypass Tone.Destination wrapper issues
    // We connect directly to the raw Hardware Output Node
    const rawDest = (effectiveContext.rawContext as AudioContext).destination;
    if (rawDest) {
        // We use 'connect' from the Tone Node, but target the Native Node
        masterGain.connect(rawDest);
    } else {
        // Fallback for non-web-audio environments (testing)
        masterGain.connect(effectiveContext.destination as any);
    }

    // 1. MASTER CHAIN
    const meter = new Tone.Meter({ context: effectiveContext });
    const masterFilter = new Tone.Filter({
        context: effectiveContext,
        frequency: 20000,
        type: "lowpass"
    }).connect(masterGain);
    masterFilter.connect(meter);

    const limiter = new Tone.Limiter({ context: effectiveContext, threshold: -1 }).connect(masterFilter);

    const compressor = new Tone.Compressor({
        context: effectiveContext,
        threshold: -20,
        ratio: 3,
        attack: 0.03,
        release: 0.1
    }).connect(limiter);

    const width = new Tone.StereoWidener({ context: effectiveContext, width: 0.5 }).connect(compressor);

    // 2. FX CHAIN & FILTERING
    const reverb = new Tone.Reverb({ context: effectiveContext, decay: 2.0, wet: 0.25 }).connect(width);

    const highPass = new Tone.Filter({
        context: effectiveContext,
        type: "highpass",
        frequency: 150,
        rolloff: -48
    }).connect(reverb);

    const delay = new Tone.FeedbackDelay({
        context: effectiveContext,
        delayTime: "8n.",
        feedback: 0.25
    }).connect(highPass);

    const sectionFilter = new Tone.Filter({
        context: effectiveContext,
        frequency: 20000,
        type: "lowpass"
    }).connect(highPass);

    // 3. SYNTHS
    const dist = new Tone.Distortion({ context: effectiveContext, distortion: 0.2 }).connect(width);

    const kick = new Tone.MembraneSynth({
        context: effectiveContext,
        pitchMemory: true,
        pitchDecay: 0.05, octaves: 4,
        volume: -3,
        envelope: { attack: 0.005, decay: 0.4, sustain: 0.01, release: 0.4 }
    } as any).connect(width);

    const snare = new Tone.NoiseSynth({
        context: effectiveContext,
        volume: -5,
        noise: { type: 'white' },
        envelope: { attack: 0.001, decay: 0.25, sustain: 0, release: 0.1 }
    } as any).connect(reverb);

    const clapDelay = new Tone.FeedbackDelay({
        context: effectiveContext,
        delayTime: 0.015,
        feedback: 0.1,
        wet: 0.3
    }).connect(reverb);

    const clapFilter = new Tone.Filter({
        context: effectiveContext,
        type: "bandpass",
        frequency: 1100,
        Q: 1
    }).connect(clapDelay);

    const clap = new Tone.NoiseSynth({
        context: effectiveContext,
        volume: -2,
        noise: { type: 'pink' },
        envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.1 }
    } as any).connect(clapFilter);

    const STANDARD_ENVELOPE = { attack: 0.01, decay: 0.1, sustain: 0.4, release: 0.05 };

    // PolySynth needs to have the voice context set during construction of its children
    // Tone.js handles this if we pass the context in the options object.
    // NOTE: Do not increase maxPolyphony limits above these values. 
    // Higher polyphony causes audio buffer underruns ("fuzzy" sound) and game freezes 
    // likely due to CPU overload or memory leaks in voice allocation.
    const midiLead = new Tone.PolySynth(Tone.Synth, {
        context: effectiveContext,
        volume: -4,
        oscillator: { type: "triangle" },
        envelope: STANDARD_ENVELOPE
    } as any);
    midiLead.maxPolyphony = 8;
    midiLead.connect(delay);

    const midiPiano = new Tone.PolySynth(Tone.Synth, {
        context: effectiveContext,
        volume: -4,
        oscillator: { type: "triangle" },
        envelope: { ...STANDARD_ENVELOPE, decay: 0.3, sustain: 0.2, release: 0.1 }
    } as any);
    midiPiano.maxPolyphony = 8;
    midiPiano.connect(highPass);

    const midiBass = new Tone.PolySynth(Tone.MonoSynth, {
        context: effectiveContext,
        volume: -4,
        oscillator: { type: "sine" },
        portamento: 0.05,
        envelope: { attack: 0.05, decay: 0.2, sustain: 0.6, release: 0.1 },
        filter: { type: "lowpass", Q: 0, rolloff: -12 },
        filterEnvelope: {
            attack: 0.01, decay: 0.2, sustain: 1, release: 0.1,
            baseFrequency: 200, octaves: 1, exponent: 1
        }
    } as any);
    midiBass.maxPolyphony = 4;
    midiBass.connect(width);

    const midiPads = new Tone.PolySynth(Tone.Synth, {
        context: effectiveContext,
        volume: -6,
        oscillator: { type: "sine" },
        envelope: { attack: 0.2, decay: 0.3, sustain: 0.8, release: 0.3 }
    } as any);
    midiPads.maxPolyphony = 12;
    midiPads.connect(sectionFilter);

    const midiStrings = new Tone.PolySynth(Tone.Synth, {
        context: effectiveContext,
        volume: -6,
        oscillator: { type: "fatsawtooth", count: 2, spread: 10 },
        envelope: { attack: 0.2, decay: 0.3, sustain: 0.7, release: 0.3 }
    } as any);
    midiStrings.maxPolyphony = 6;
    midiStrings.connect(highPass);

    const midiBrass = new Tone.PolySynth(Tone.MonoSynth, {
        context: effectiveContext,
        volume: -6,
        oscillator: { type: "pulse", width: 0.6 },
        envelope: { attack: 0.05, decay: 0.2, sustain: 0.5, release: 0.15 },
        filter: { type: "lowpass", Q: 1, rolloff: -24 },
        filterEnvelope: {
            attack: 0.05, decay: 0.2, sustain: 0.3, release: 0.15,
            baseFrequency: 600, octaves: 2.5, exponent: 2
        }
    } as any);
    midiBrass.maxPolyphony = 6;
    midiBrass.connect(highPass);

    const midiWind = new Tone.PolySynth(Tone.Synth, {
        context: effectiveContext,
        volume: -4,
        oscillator: { type: "sine" },
        envelope: { attack: 0.05, decay: 0.1, sustain: 0.8, release: 0.1 }
    } as any);
    midiWind.maxPolyphony = 8;
    midiWind.connect(highPass);

    const uiSynth = new Tone.PolySynth(Tone.Synth, { context: effectiveContext } as any).connect(reverb);
    uiSynth.maxPolyphony = 32;

    const noiseSynth = new Tone.NoiseSynth({
        context: effectiveContext,
        envelope: { attack: 0.01, decay: 0.1, sustain: 0, release: 0.1 },
        volume: -5
    } as any).connect(reverb);

    const sub = new Tone.MonoSynth({
        context: effectiveContext,
        volume: -5,
        oscillator: { type: "sine" },
        envelope: { attack: 0.1, decay: 0.3, sustain: 1, release: 0.8 }
    } as any).connect(masterFilter);

    const pads = new Tone.PolySynth(Tone.Synth, {
        context: effectiveContext,
        volume: -15,
        oscillator: { type: "fatsawtooth", count: 3, spread: 20 },
        envelope: { attack: 0.5, decay: 0.3, sustain: 0.8, release: 1.0 }
    } as any);
    pads.maxPolyphony = 16;
    pads.connect(sectionFilter);

    const hihat = new Tone.MetalSynth({
        context: effectiveContext,
        volume: -10,
        envelope: { attack: 0.001, decay: 0.1, release: 0.01 }
    } as any).connect(reverb);
    const crash = new Tone.MetalSynth({
        context: effectiveContext,
        volume: -5,
        harmonicity: 12,
        resonance: 800,
        envelope: { decay: 1.5, release: 1 }
    } as any).connect(reverb);
    const bass = new Tone.FMSynth({ context: effectiveContext } as any).connect(dist);
    const piano = new Tone.PolySynth(Tone.Synth, { context: effectiveContext } as any).connect(reverb);
    const lead = new Tone.Synth({ context: effectiveContext } as any).connect(delay);
    const pluck = new Tone.PolySynth(Tone.Synth, { context: effectiveContext } as any).connect(delay);

    return {
        kick, snare, clap, hihat, crash, bass, sub, pads, piano, lead, pluck,
        midiLead, midiPiano, midiBass, midiPads, midiStrings, midiBrass, midiWind,
        uiSynth, noiseSynth,
        dist, delay, clapDelay, clapFilter, // RETURNED HERE
        reverb, sectionFilter, masterFilter, compressor, limiter, width, meter, highPass, masterGain
    } as any;
};
